import {
  AuthRepositoryInterface,
  SignInParameter,
  ConfirmSignUpParameter,
  ForgotPasswordParameter,
  CompleteNewPasswordParameter,
  ForgotPasswordSubmitParameter,
  ResendSignUpParameter,
  ChangePasswordParameter,
  UpdateUserAttributesParameter,
  SignUpParameter,
} from "./auth.repository.d";
import { Auth } from "aws-amplify";
import queryString from "query-string";
import { SignedInDto } from "./dto/signedIn.dto";
import { ForgotPasswordDto } from "./dto/forgotPassword.dto";
import { BaseRepository } from "../Base/base.repository";
import { ServerlessConfigure } from "../../config/serverless-configure";
import { SignUpDto } from "./dto/signUp.dto";
import {
  DownloadUrlDto,
  GetPresignedUrlParameter,
  PresignedUrlDto,
} from "./dto/presignedUrl.dto";
import { ApiRequest } from "../../modules/ApiRequest/ApiRequest";

export class AuthRepository
  extends BaseRepository<undefined>
  implements AuthRepositoryInterface
{
  constructor() {
    super(
      {
        baseURL: ServerlessConfigure.ApiEndpoint,
      },
      {
        hasSendToken: true,
      },
    );
  }

  async signIn(params: SignInParameter): Promise<SignedInDto> {
    const { username, password } = params;
    const userInfo = await Auth.signIn(username, password);
    if (userInfo.signInUserSession) {
      userInfo.group =
        userInfo.signInUserSession.idToken.payload["cognito:groups"][0];
    }
    return userInfo;
  }

  async signUp(params: SignUpParameter) {
    const { clientId, tenantId, username, password, firstName, lastName } =
      params;
    const response = await this.apiRequest.post<SignUpDto>({
      url: "/auth/signUp",
      data: {
        clientId,
        tenantId,
        username,
        password,
        firstName,
        lastName,
      },
    });
    Auth.signUp({
      username,
      password,
    });
    return response.data;
  }

  async confirmSignUp(params: ConfirmSignUpParameter) {
    const { clientId, tenantId, username, code } = params;
    const response = await this.apiRequest.post({
      url: "/auth/confirmSignUp",
      data: {
        clientId,
        tenantId,
        username,
        code,
      },
    });
    return response.data;
  }

  resendSignUp(params: ResendSignUpParameter) {
    const { username } = params;
    return Auth.resendSignUp(username);
  }

  async forgotPassword(
    params: ForgotPasswordParameter,
  ): Promise<ForgotPasswordDto> {
    const { email, clientId, tenantId } = params;
    const response = await this.apiRequest.post<ForgotPasswordDto>({
      url: "/auth/forgotPassword",
      data: {
        username: email,
        clientId,
        tenantId,
      },
    });
    return response.data;
  }

  forgotPasswordSubmit(params: ForgotPasswordSubmitParameter) {
    const { username, code, password } = params;
    return Auth.forgotPasswordSubmit(username, code, password);
  }

  async completeNewPassword(
    params: CompleteNewPasswordParameter,
  ): Promise<boolean> {
    const { cognitoUser, password } = params;
    await this.apiRequest.post({
      url: "/auth/active",
      data: {
        newPassword: password,
        username: cognitoUser.username,
        session: cognitoUser.Session,
        clientId: cognitoUser.pool?.clientId,
        userPoolId: cognitoUser.pool?.userPoolId,
      },
    });
    return true;
  }

  async currentAuthenticatedUser(bypassCache = false): Promise<SignedInDto> {
    const userInfo = await Auth.currentAuthenticatedUser({ bypassCache });
    userInfo.group =
      userInfo.signInUserSession.idToken.payload["cognito:groups"][0];
    return userInfo;
  }

  signOut() {
    return Auth.signOut();
  }

  changePassword(params: ChangePasswordParameter) {
    const { user, oldPassword, newPassword } = params;
    return Auth.changePassword(user, oldPassword, newPassword);
  }

  updateUserAttributes(params: UpdateUserAttributesParameter) {
    const { user, attrs } = params;

    const partialAttrs: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(attrs)) {
      if (value !== undefined) {
        partialAttrs[key] = value;
      }
    }
    return Auth.updateUserAttributes(user, partialAttrs);
  }

  async getPresignedUrl(
    params: GetPresignedUrlParameter,
  ): Promise<PresignedUrlDto> {
    const { tenantId, fileStorage, fileType } = params;
    const apiRequest = new ApiRequest<PresignedUrlDto>(
      {
        baseURL: ServerlessConfigure.ApiEndpoint,
      },
      {
        hasSendToken: true,
      },
    );

    const queryStringParams = queryString.stringify({
      tenantId,
      fileStorage,
      fileType,
    });

    const response = await apiRequest.get({
      url: `/auth/preSignedUrl?${queryStringParams}`,
    });
    return response.data;
  }

  async getDownloadUrl(s3Uri: string): Promise<DownloadUrlDto> {
    const apiRequest = new ApiRequest<DownloadUrlDto>(
      {
        baseURL: ServerlessConfigure.ApiEndpoint,
      },
      {
        hasSendToken: true,
      },
    );

    const queryStringParams = queryString.stringify({ s3Uri });
    const response = await apiRequest.get({
      url: `/auth/preSignedDownloadUrl?${queryStringParams}`,
    });
    return response.data;
  }
}
